import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Factory, ShieldCheck, Globe, ChevronRight, Cpu, Zap, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS, scrollToSection } from '@/lib/index';
import { productionLines, coatingFacilities, companyStats } from '@/data/index';
import { ProductionLineCard, CoatingFacilityCard, ProductCard, StatsCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { Button } from '@/components/ui/button';
import { LMEWidget } from '@/components/LMEWidget';

export default function HomeEn() {
  // English Production Lines Data
  const productionLinesEn = [
    {
      id: "line-4",
      title: "4 Inch Extrusion Line",
      description: "Optimized for high precision small and medium scale profile production.",
      capacity: "2400 Tons/Year",
      technology: "PLC-V5",
      type: "extrusion" as const,
      specifications: [
        "Minimum wall thickness: 0.8 mm",
        "Maximum profile width: 110 mm",
        "Automatic cutting and cooling system"
      ],
      image: "/images/Gemini_Generated_Image_wl5sy6wl5sy6wl5s.png"
    },
    {
      id: "line-5",
      title: "5 Inch Extrusion Line",
      description: "Flexible production capacity enabling fast cycle times for standard architectural profiles.",
      capacity: "3600 Tons/Year",
      technology: "PLC-V5",
      type: "extrusion" as const,
      specifications: [
        "Minimum wall thickness: 1.0 mm",
        "Maximum profile width: 140 mm",
        "PLC controlled process management"
      ],
      image: "/images/indir (1).jpg"
    },
    {
      id: "line-6",
      title: "6 Inch Extrusion Line",
      description: "Our main production line offering high efficiency for industrial profiles and wide-surface architectural systems.",
      capacity: "5400 Tons/Year",
      technology: "PLC-V5",
      type: "extrusion" as const,
      specifications: [
        "Minimum wall thickness: 1.2 mm",
        "Maximum profile width: 180 mm",
        "High-speed puller system"
      ],
      image: "/images/Ekran görüntüsü 2026-02-07 202330.png"
    },
    {
      id: "line-7",
      title: "7 Inch Extrusion Line",
      description: "High-pressure line designed for large-scale structural components and heavy industry profiles.",
      capacity: "7200 Tons/Year",
      technology: "PLC-V5",
      type: "extrusion" as const,
      specifications: [
        "Minimum wall thickness: 1.5 mm",
        "Maximum profile width: 220 mm",
        "Double puller technology"
      ],
      image: "/images/Ekran görüntüsü 2026-02-07 202330.png"
    }
  ];

  // English Coating Facilities Data
  const coatingFacilitiesEn = [
    {
      id: "coating-h1",
      title: "Horizontal Powder Coating Line 1",
      description: "High-capacity horizontal coating line with advanced pretreatment and curing systems for architectural profiles.",
      capacity: "8000 Tons/Year",
      technology: "Automated",
      type: "coating" as const,
      specifications: [
        "Chromate-free pretreatment",
        "Electrostatic powder application",
        "Convection curing oven"
      ],
      image: "/images/boyahane-konveyor-sistemleri-ZHjL0tvuMw2uLU89TL8u.jpg"
    },
    {
      id: "coating-h2",
      title: "Horizontal Powder Coating Line 2",
      description: "Secondary horizontal line optimized for industrial profiles and special color applications.",
      capacity: "6000 Tons/Year",
      technology: "Semi-Automated",
      type: "coating" as const,
      specifications: [
        "Multi-stage pretreatment",
        "Dual powder booth system",
        "Energy-efficient curing"
      ],
      image: "/images/görsel_2026-02-09_111820137.png"
    },
    {
      id: "coating-v1",
      title: "Vertical Powder Coating Line",
      description: "Specialized vertical coating system for long profiles and architectural curtain wall systems.",
      capacity: "7000 Tons/Year",
      technology: "Fully Automated",
      type: "coating" as const,
      specifications: [
        "Vertical transport system",
        "Precision powder application",
        "Qualicoat certified process"
      ],
      image: "/images/görsel_2026-02-09_110457202.png"
    }
  ];

  // English Product Categories Data
  const productCategoriesEn = [
    {
      id: "architectural",
      title: "Architectural Systems",
      description: "Aesthetic, durable and highly insulated aluminum solutions for modern buildings.",
      features: [
        "Thermally Insulated Door and Window Systems",
        "Curtain Wall Systems",
        "Sliding and Folding Door Systems"
      ],
      image: "/images/office part.jpg",
      path: "/en/products"
    },
    {
      id: "facade",
      title: "Facade and Joinery Systems",
      description: "Aesthetic and functional facade solutions suitable for the needs of modern architecture.",
      features: [
        "Advanced Technology Curtain Wall Systems",
        "Structural Glazing Systems",
        "Curtain Wall Systems"
      ],
      image: "/images/composite.jpg",
      path: "/en/products"
    },
    {
      id: "industrial",
      title: "Industrial Profiles",
      description: "Technical profiles specific to every sector from automotive to energy, from white goods to machinery manufacturing.",
      features: [
        "Solar Panel Profiles",
        "Automotive and Transportation Sector Profiles",
        "Machine and Chassis Profiles"
      ],
      image: "/images/otomotiv.jpg",
      path: "/en/products"
    }
  ];
  const heroSlides = [
    { src: IMAGES.HERO_INDUSTRIAL_4,    alt: "Industrial Aluminum Factory" },
    { src: IMAGES.ALUMINYUM_BILLET_NEDIR, alt: "Aluminum Billet" },
    { src: IMAGES.ALUMINIUM_INGOT_99_7,   alt: "Aluminum Ingot" },
  ];
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return <div className="flex flex-col w-full overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] w-full flex items-center">
        <div className="absolute inset-0 z-0 overflow-hidden">
          {heroSlides.map((slide, index) => (
            <img
              key={index}
              src={slide.src}
              alt={slide.alt}
              className="absolute inset-0 w-full h-full object-cover brightness-[0.4] transition-opacity duration-1000"
              style={{ opacity: index === currentSlide ? 1 : 0 }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={springPresets.smooth} className="max-w-3xl text-white">
            <span className="inline-block py-1 px-3 rounded-sm bg-accent/20 border border-accent/30 text-accent text-sm font-medium mb-6 uppercase tracking-widest">
              Future Aluminum Technologies
            </span>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              Pushing Boundaries <br />
              <span className="text-accent">Integrated Production</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-10 max-w-2xl font-light">
              From architectural systems to industrial profiles, we produce to global standards with our 4" - 5" - 6" - 7" extrusion lines and high-capacity coating facilities.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-bold px-8 h-14">
                <Link to={ROUTE_PATHS.CONTACT_EN}>
                  Request Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8 h-14" onClick={() => scrollToSection('integrated-production')}>
                Explore Production
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Floating Stats Quick View */}
        {/* Slideshow Dots */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 z-10 lg:bottom-28">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-accent w-8'
                  : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>

        <div className="absolute bottom-0 left-0 w-full bg-white/5 backdrop-blur-md border-t border-white/10 hidden lg:block">
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-4 gap-8">
              {companyStats.map(stat => <div key={stat.id} className="text-white">
                  <p className="text-3xl font-bold text-accent">{stat.value}{stat.suffix}</p>
                  <p className="text-sm text-gray-400 font-medium uppercase tracking-wider">
                    {stat.id === 'experience' ? 'Years Experience' : 
                     stat.id === 'capacity' ? 'Tons Annual Capacity' : 
                     stat.id === 'employees' ? 'Expert Employees' : 
                     stat.id === 'customers' ? 'Satisfied Customers' : stat.label}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* LME Widget */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <LMEWidget />
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp}>
              <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
                <div className="h-1 w-12 bg-primary" />
                Corporate Overview
              </div>
              <h2 className="text-4xl font-bold mb-8 text-primary">
                5 Years of Growth, <br />
                Modern Engineering Approach
              </h2>
              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p>
                  Founded in 2020, MIMALUM has rapidly established itself as one of Turkey's dynamic aluminum extrusion manufacturers. In our facilities built on 26,000 m² closed area, we bring together all processes from raw material to final product under one roof.
                </p>
                <p>
                  We don't just produce profiles, we provide engineering solutions. With our mold design, foundry, extrusion, surface treatment and mechanical processing units, we respond to our customers' most complex needs.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6 mt-10">
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-primary/5 rounded-lg">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary">High Quality</h4>
                    <p className="text-sm text-muted-foreground">Qualicoat and ISO standards</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-primary/5 rounded-lg">
                    <Globe className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary">Global Reach</h4>
                    <p className="text-sm text-muted-foreground">Export to 45+ countries</p>
                  </div>
                </div>
              </div>
              <Button asChild variant="link" className="mt-8 p-0 text-primary font-bold hover:no-underline">
                <Link to={ROUTE_PATHS.COMPANY_EN} className="flex items-center">
                  Review Company Profile <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative">
              <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
                <img src="/images/Gemini_Generated_Image_wl5sy6wl5sy6wl5s.png" alt="Extrusion Line" className="w-full h-[333px] object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-primary p-10 rounded-xl text-white hidden md:block">
                <div className="text-5xl font-bold text-accent mb-2">5+</div>
                <div className="text-sm font-medium uppercase tracking-widest opacity-80">Years Industry <br /> Experience</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrated Production (Extrusion) */}
      <section id="integrated-production" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="mb-16">
            <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
              <div className="h-1 w-12 bg-primary" />
              Production Technologies
            </div>
            <h2 className="text-4xl font-bold text-primary">Integrated Extrusion Lines</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl">
              With our 4 modern extrusion lines of different scales, we produce profiles suitable for all kinds of technical and aesthetic requirements.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-100px"
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productionLinesEn.map(line => <motion.div key={line.id} variants={staggerItem}>
                <ProductionLineCard line={line} language="en" />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Coating Facilities */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-16 mb-20">
            <div className="w-full lg:w-1/3">
              <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
                <div className="h-1 w-12 bg-primary" />
                Surface Treatment
              </div>
              <h2 className="text-4xl font-bold text-primary mb-6">Surface Treatment and Coating Facilities</h2>
              <p className="text-muted-foreground mb-8">
                With our 2 horizontal and 1 vertical powder coating lines, we have an annual coating capacity of over 21,000 tons. We offer unlimited color options in Qualicoat Seaside standards.
              </p>
              <ul className="space-y-4">
                {['2 Horizontal Powder Coating Lines', '1 Vertical Powder Coating Line', 'Chromate-Free Surface Preparation', 'Wide RAL Color Chart Options'].map((item, idx) => <li key={idx} className="flex items-center gap-3 font-medium text-primary">
                    <div className="h-2 w-2 rounded-full bg-accent" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="w-full lg:w-2/3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {coatingFacilitiesEn.map(facility => <CoatingFacilityCard key={facility.id} facility={facility} language="en" />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-24 bg-primary text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none">
          <Layers className="w-full h-full stroke-[0.5]" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Product Groups</h2>
            <p className="text-primary-foreground/70 max-w-2xl mx-auto">
              At the intersection of engineering and aesthetics, we produce custom aluminum solutions for your project.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {productCategoriesEn.map(category => <ProductCard key={category.id} category={category} />)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-accent">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="text-primary">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Technical Support for Your Project?</h2>
              <p className="text-lg font-medium opacity-80">
                Our expert engineering team is with you from design stage to production.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90 px-10 h-16 text-lg">
                <Link to={ROUTE_PATHS.CONTACT_EN}>Request Quote</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/5 px-10 h-16 text-lg">
                <Link to={ROUTE_PATHS.DOWNLOAD_CENTER_EN}>Download Catalogs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Highlight */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center p-4 bg-green-50 rounded-full mb-8">
            <Zap className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-primary mb-6">100% Recyclable Future</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto mb-10 text-lg">
            Aluminum can be recycled infinitely. We minimize our carbon footprint by using environmentally friendly technologies in our production facilities and produce green aluminum for a sustainable world.
          </p>
          <Button asChild variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
            <Link to={ROUTE_PATHS.SUSTAINABILITY_EN}>Our Sustainability Vision</Link>
          </Button>
        </div>
      </section>
    </div>;
}